/**
 * =============================================================================
 * WlFinancialStatusFilter
 *
 * Filtro multi-select de status para o painel WL Financeiro. Trigger compacto
 * (mesmo padrao dos filtros de listagem WL) com Popover + Checkbox e chip
 * mostrando o resumo da selecao. Sempre mantem pelo menos 1 status selecionado
 * (padrao "Ativo"), pois sem status o dashboard perde sentido.
 *
 * @version 1.2.0
 * RELEASE:
 *   2026-07-22 v1.2.0 - Adiciona opcoes Cancelado, Atrasado e Inadimplente,
 *                       que ja sao aceitas pelas RPCs wl_get_financial_kpis
 *                       (v1.8.0) e wl_list_tenant_subscriptions (v1.6.0) via
 *                       normalizacao de p_statuses. Sem alteracao de SQL.
 *   2026-07-22 v1.1.0 - Alinhamento das opcoes de status com os valores reais
 *                       existentes em `tenants.status` / `tenants.subscription_status`.
 *   2026-07-21 v1.0.0 - Versao inicial.
 * =============================================================================
 */
import { useMemo } from 'react';
import { Check, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

/**
 * Opcoes disponiveis. Valores refletem os enums normalizados pelas RPCs
 * WL Financeiro (eff_status), cobrindo tanto o dominio de tenants quanto o
 * legado financeiro (fa.status). Labels em portugues para o usuario final.
 */
export const WL_FINANCIAL_STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active',       label: 'Ativo' },
  { value: 'trial',        label: 'Trial' },
  { value: 'past_due',     label: 'Atrasado' },
  { value: 'unpaid',       label: 'Inadimplente' },
  { value: 'canceled',     label: 'Cancelado' },
  { value: 'suspended',    label: 'Suspenso' },
  { value: 'inactive',     label: 'Inativo' },
];

/** Default aplicado quando o usuario limpa o filtro — nunca vazio. */
export const WL_FINANCIAL_STATUS_DEFAULT: string[] = ['active'];

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  className?: string;
}

/** Multi-select compacto para status de tenants pagantes. */
export function WlFinancialStatusFilter({ value, onChange, className }: Props) {
  // Rotulo curto para o botao — mostra ate 2 labels selecionados, depois "+N".
  const summary = useMemo(() => {
    if (!value || value.length === 0) return 'Status';
    const labels = value
      .map(v => WL_FINANCIAL_STATUS_OPTIONS.find(o => o.value === v)?.label ?? v);
    if (labels.length <= 2) return labels.join(', ');
    return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
  }, [value]);

  /** Alterna um valor. Nunca deixa a lista vazia. */
  const toggle = (val: string) => {
    const isOn = value.includes(val);
    let next: string[];
    if (isOn) {
      next = value.filter(v => v !== val);
      if (next.length === 0) next = [...WL_FINANCIAL_STATUS_DEFAULT];
    } else {
      next = [...value, val];
    }
    onChange(next);
  };

  const reset = () => onChange([...WL_FINANCIAL_STATUS_DEFAULT]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-8 gap-2 text-xs', className)}
        >
          <Filter className="h-3.5 w-3.5" />
          <span className="truncate max-w-[180px]">Status: {summary}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2">
        <div className="space-y-1">
          {WL_FINANCIAL_STATUS_OPTIONS.map(opt => {
            const checked = value.includes(opt.value);
            return (
              <label
                key={opt.value}
                className="flex items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-muted cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  onCheckedChange={() => toggle(opt.value)}
                />
                <span className="flex-1">{opt.label}</span>
                {checked && <Check className="h-3 w-3 text-primary" />}
              </label>
            );
          })}
        </div>
        <div className="mt-2 border-t pt-2 flex justify-end">
          <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={reset}>
            Restaurar padrao
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
